"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Calendar, Clock, ArrowRight, Tag } from "lucide-react";
import { getBlogPosts, getAllCategories, getAllTags } from "@/lib/blog";

// Metadata will be handled by the parent layout

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [blogPosts, blogCategories, blogTags] = await Promise.all([
          getBlogPosts(),
          getAllCategories(),
          getAllTags(),
        ]);

        console.log("Loaded posts:", blogPosts);
        console.log("Loaded categories:", blogCategories);
        console.log("Loaded tags:", blogTags);

        setPosts(blogPosts);
        setCategories(blogCategories);
        setTags(blogTags);
      } catch (error) {
        console.error("Error loading blog data:", error);
      } finally {
        setLoading(false);
      }
    };

    // Load data immediately
    loadData();

    // Refresh when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Small delay to ensure any pending updates are processed
        setTimeout(loadData, 100);
      }
    };

    // Listen for custom event when posts are updated in the same tab
    const handleBlogPostsUpdated = () => {
      console.log("Blog posts updated event received");
      // Small delay to ensure the array is updated
      setTimeout(loadData, 50);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blogPostsUpdated", handleBlogPostsUpdated);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blogPostsUpdated", handleBlogPostsUpdated);
    };
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-accent-600 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Our Blog</h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              Insights, trends, and tips in mobile app development. Stay updated
              with the latest industry news and best practices.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-8">
              {/* Categories */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                  Categories
                </h3>
                <ul className="space-y-2">
                  {categories.map((category) => (
                    <li key={category}>
                      <Link
                        href={`/blog?category=${encodeURIComponent(category)}`}
                        className="text-secondary-600 hover:text-primary-600 transition-colors duration-200"
                      >
                        {category}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tags */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                  Popular Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.slice(0, 10).map((tag) => (
                    <Link
                      key={tag}
                      href={`/blog?tag=${encodeURIComponent(tag)}`}
                      className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full hover:bg-primary-200 transition-colors duration-200"
                    >
                      <Tag className="w-3 h-3" />
                      <span>{tag}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Blog Posts */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-secondary-600">Loading blog posts...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-secondary-400 text-2xl">📝</span>
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                  No Blog Posts Yet
                </h3>
                <p className="text-secondary-600">
                  Check back later for new content!
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-8">
                  {posts.map((post, index) => (
                    <article
                      key={post.id}
                      className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 animate-slide-up"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="h-48 bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                            <span className="text-white font-bold text-xl">
                              {post.title.charAt(0)}
                            </span>
                          </div>
                          <span className="text-primary-600 font-medium text-sm">
                            {post.category}
                          </span>
                        </div>
                      </div>

                      <div className="p-8">
                        <div className="flex items-center space-x-4 text-sm text-secondary-500 mb-4">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(post.publishedAt)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{post.readTime}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span>By {post.author}</span>
                          </div>
                        </div>

                        <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                          {post.title}
                        </h2>

                        <p className="text-secondary-600 mb-6 text-lg">
                          {post.excerpt}
                        </p>

                        <div className="flex flex-wrap gap-2 mb-6">
                          {post.tags.map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="px-3 py-1 bg-secondary-100 text-secondary-700 text-sm rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        <Link
                          href={`/blog/${post.slug}`}
                          className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
                        >
                          <span>Read Full Article</span>
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>

                {/* Pagination */}
                <div className="mt-12 flex justify-center">
                  <div className="flex space-x-2">
                    <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                      1
                    </button>
                    <button className="px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 transition-colors">
                      2
                    </button>
                    <button className="px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 transition-colors">
                      3
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
